import { useEffect, useState } from 'react';
import { llmConfigApi, type LLMConfig, type ProviderPreset } from '../api';
import {
  Cpu, Save, PlugZap, Trash2, Eye, EyeOff, ChevronDown, ChevronUp,
  Check, AlertCircle, Loader2, Info
} from 'lucide-react';

export default function LLMSettings() {
  // 预设列表
  const [presets, setPresets] = useState<ProviderPreset[]>([]);

  // 表单状态
  const [providerName, setProviderName] = useState('siliconflow');
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiKeyMasked, setApiKeyMasked] = useState('');
  const [model, setModel] = useState('');
  const [visionModel, setVisionModel] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(2000);

  // UI 状态
  const [showApiKey, setShowApiKey] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [hasExisting, setHasExisting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  // 加载预设 + 当前配置
  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [presetsRes, configRes] = await Promise.all([
        llmConfigApi.getPresets(),
        llmConfigApi.get(),
      ]);
      const presetList = presetsRes.data.data;
      setPresets(presetList);

      const config = configRes.data.data;
      if (config) {
        setHasExisting(true);
        setProviderName(config.provider_name);
        setBaseUrl(config.base_url);
        setApiKeyMasked(config.api_key_masked || '');
        setApiKey(''); // 不回显明文
        setModel(config.model);
        setVisionModel(config.vision_model || '');
        setTemperature(config.temperature ?? 0.7);
        setMaxTokens(config.max_tokens ?? 2000);
      } else {
        // 默认选第一个预设
        if (presetList.length > 0) {
          applyPreset(presetList[0]);
        }
      }
    } catch {
      setMessage({ type: 'error', text: '加载配置失败，请刷新重试' });
    } finally {
      setLoading(false);
    }
  }

  function applyPreset(preset: ProviderPreset) {
    setProviderName(preset.name);
    setBaseUrl(preset.baseUrl);
    setModel(preset.defaultModel);
    setVisionModel(preset.visionModels[0] || '');
  }

  function handlePresetChange(name: string) {
    const preset = presets.find(p => p.name === name);
    if (preset) {
      applyPreset(preset);
    }
  }

  const currentPreset = presets.find(p => p.name === providerName);
  const modelOptions = currentPreset?.models || [];
  const visionModelOptions = currentPreset?.visionModels || [];

  async function handleTest() {
    if (!apiKey && !apiKeyMasked) {
      setMessage({ type: 'error', text: '请输入 API Key' });
      return;
    }
    if (!apiKey) {
      setMessage({ type: 'info', text: '请重新输入 API Key 后再测试（已保存的 Key 不会回显）' });
      return;
    }
    setTesting(true);
    setMessage(null);
    try {
      const res = await llmConfigApi.validate({ base_url: baseUrl, api_key: apiKey, model });
      const result = res.data;
      if (result.valid) {
        setMessage({ type: 'success', text: `连接测试成功！延迟: ${result.latency}ms` });
      } else {
        setMessage({ type: 'error', text: `连接失败: ${result.error}` });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || '测试请求失败' });
    } finally {
      setTesting(false);
    }
  }

  async function handleSave() {
    if (!baseUrl || !model || !providerName) {
      setMessage({ type: 'error', text: '请填写提供商、API Base URL 和模型' });
      return;
    }
    if (!apiKey && !hasExisting) {
      setMessage({ type: 'error', text: '请输入 API Key' });
      return;
    }
    if (!apiKey && hasExisting) {
      setMessage({ type: 'info', text: '未输入新 API Key，将保留原有 Key' });
      // Allow saving other fields without re-entering key — need to re-enter for now
      // because backend requires api_key on PUT
      setMessage({ type: 'error', text: '修改配置时需要重新输入 API Key（已保存的 Key 不会回显）' });
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      await llmConfigApi.save({
        provider_name: providerName,
        base_url: baseUrl,
        api_key: apiKey,
        model,
        vision_model: visionModel || undefined,
        temperature,
        max_tokens: maxTokens,
      });
      setHasExisting(true);
      setApiKeyMasked(apiKey.substring(0, Math.min(8, apiKey.length)) + '...');
      setMessage({ type: 'success', text: '配置已保存！' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || '保存失败' });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirm('确定删除 LLM 配置？删除后将使用系统默认配置。')) return;
    setDeleting(true);
    setMessage(null);
    try {
      await llmConfigApi.delete();
      setHasExisting(false);
      setApiKey('');
      setApiKeyMasked('');
      // 重置为第一个预设
      if (presets.length > 0) {
        applyPreset(presets[0]);
      }
      setMessage({ type: 'success', text: '配置已删除，将使用系统默认配置' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || '删除失败' });
    } finally {
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-500">加载中...</span>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-3 md:p-6">
      {/* 标题 */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
          <Cpu className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">AI 模型配置</h1>
          <p className="text-sm text-gray-500">配置你自己的 LLM 提供商和 API Key</p>
        </div>
      </div>

      {/* 系统配置提示 */}
      {!hasExisting && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-700">
          <Info size={16} className="mt-0.5 flex-shrink-0" />
          <span>当前使用系统默认配置。配置自己的 API Key 后，AI 功能将使用你的账户调用。</span>
        </div>
      )}

      {/* 表单 */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm divide-y divide-gray-100">
        {/* 提供商选择 */}
        <div className="p-5">
          <label className="block text-sm font-medium text-gray-700 mb-2">LLM 提供商</label>
          <select
            value={providerName}
            onChange={e => handlePresetChange(e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            {presets.map(p => (
              <option key={p.name} value={p.name}>{p.label}</option>
            ))}
          </select>
        </div>

        {/* API Base URL */}
        <div className="p-5">
          <label className="block text-sm font-medium text-gray-700 mb-2">API Base URL</label>
          <input
            type="text"
            value={baseUrl}
            onChange={e => setBaseUrl(e.target.value)}
            placeholder="https://api.example.com/v1"
            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          {providerName === 'custom' && (
            <p className="mt-1.5 text-xs text-gray-400">自定义提供商请输入兼容 OpenAI 格式的 API 地址</p>
          )}
        </div>

        {/* API Key */}
        <div className="p-5">
          <label className="block text-sm font-medium text-gray-700 mb-2">API Key</label>
          <div className="relative">
            <input
              type={showApiKey ? 'text' : 'password'}
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder={hasExisting ? `已保存 (${apiKeyMasked})` : '请输入 API Key'}
              className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              type="button"
              onClick={() => setShowApiKey(!showApiKey)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <p className="mt-1.5 text-xs text-gray-400">API Key 经加密后存储，不会明文保存</p>
        </div>

        {/* 对话模型 */}
        <div className="p-5">
          <label className="block text-sm font-medium text-gray-700 mb-2">对话模型</label>
          {modelOptions.length > 0 ? (
            <select
              value={model}
              onChange={e => setModel(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              {modelOptions.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={model}
              onChange={e => setModel(e.target.value)}
              placeholder="模型名称，如 gpt-4o"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          )}
        </div>

        {/* 高级设置 */}
        <div className="p-5">
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-800"
          >
            {showAdvanced ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            高级设置
          </button>

          {showAdvanced && (
            <div className="mt-4 space-y-4 pl-1">
              {/* Vision 模型 */}
              <div>
                <label className="block text-sm text-gray-600 mb-1.5">Vision 模型（OCR 识别用）</label>
                {visionModelOptions.length > 0 ? (
                  <select
                    value={visionModel}
                    onChange={e => setVisionModel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">使用默认</option>
                    {visionModelOptions.map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={visionModel}
                    onChange={e => setVisionModel(e.target.value)}
                    placeholder="留空使用默认 (Qwen3-VL-32B)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                )}
              </div>

              {/* Temperature */}
              <div>
                <label className="block text-sm text-gray-600 mb-1.5">
                  Temperature: <span className="font-mono text-blue-600">{temperature.toFixed(1)}</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={temperature}
                  onChange={e => setTemperature(parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>精确 (0)</span>
                  <span>创意 (2)</span>
                </div>
              </div>

              {/* Max Tokens */}
              <div>
                <label className="block text-sm text-gray-600 mb-1.5">最大输出 Tokens</label>
                <input
                  type="number"
                  min={100}
                  max={32000}
                  step={100}
                  value={maxTokens}
                  onChange={e => setMaxTokens(parseInt(e.target.value) || 2000)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 消息提示 */}
      {message && (
        <div className={`mt-4 flex items-center gap-2 px-4 py-3 rounded-xl text-sm ${
          message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' :
          message.type === 'error' ? 'bg-red-50 border border-red-200 text-red-700' :
          'bg-blue-50 border border-blue-200 text-blue-700'
        }`}>
          {message.type === 'success' ? <Check size={16} /> :
           message.type === 'error' ? <AlertCircle size={16} /> :
           <Info size={16} />}
          {message.text}
        </div>
      )}

      {/* 操作按钮 */}
      <div className="mt-5 flex items-center gap-3">
        <button
          onClick={handleTest}
          disabled={testing || (!apiKey && !apiKeyMasked)}
          className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {testing ? <Loader2 size={16} className="animate-spin" /> : <PlugZap size={16} />}
          测试连接
        </button>

        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          保存配置
        </button>

        {hasExisting && (
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="flex items-center gap-2 px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-xl text-sm font-medium transition-colors ml-auto"
          >
            {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
            删除配置
          </button>
        )}
      </div>
    </div>
  );
}
